import { motion } from "framer-motion";
import { MapPin, Route, CalendarClock, ShieldCheck } from "lucide-react";

const AREAS = [
  "Canton",
  "Akron",
  "Wooster",
  "New Philadelphia",
  "Alliance",
  "Surrounding Northeast Ohio communities",
];

const PERKS = [
  { icon: Route, title: "We Come To You", desc: "Training at approved parks, fields, schools & facilities near you." },
  { icon: CalendarClock, title: "Flexible Scheduling", desc: "Sessions arranged around your schedule and season." },
  { icon: ShieldCheck, title: "Same Elite Coaching", desc: "Identical high-quality, individualized programming — anywhere." },
];

export default function MobileTraining() {
  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="locations"
      data-testid="mobile-training-section"
      className="relative py-24 md:py-36 bg-[#0a0a0a] border-t border-white/10 overflow-hidden"
    >
      <div className="grain absolute inset-0 opacity-60" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
              How We Train
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
              Mobile Training Across <span className="text-white/50">Northeast Ohio</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              JJ Speed Factory brings elite speed development directly to you.
              Mobile speed and athletic performance training is available
              throughout Northeast Ohio, including Canton, Akron, Wooster, New
              Philadelphia, Alliance, and surrounding communities — at approved
              parks, athletic fields, schools, and training facilities.
            </p>
            <p className="mt-4 text-white/55 leading-relaxed">
              Whether you're an individual athlete, a team, or a small group, we'll
              work together to find a safe and convenient training location that
              fits your schedule — while receiving the same high-quality coaching
              and individualized programming every time.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {PERKS.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.title} className="border border-white/10 p-5">
                    <Icon size={22} className="text-white/80 mb-3" strokeWidth={1.5} />
                    <h3 className="font-display font-bold uppercase tracking-tight text-lg leading-none mb-1">
                      {p.title}
                    </h3>
                    <p className="text-white/50 text-sm">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="bg-black border border-white/10 p-8 md:p-10"
          >
            <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-white/40 mb-6">
              Service Area Includes
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              {AREAS.map((a, i) => (
                <li key={a} className="flex items-center gap-3 text-white/80" data-testid={`area-${i}`}>
                  <MapPin size={18} className="text-white/60 shrink-0" />
                  <span className="text-base">{a}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="text-white/60 text-sm leading-relaxed">
                Don't see your city listed?{" "}
                <button
                  data-testid="mobile-contact-link"
                  onClick={() => scrollTo("#contact")}
                  className="text-white underline underline-offset-4 hover:text-white/70 transition-colors"
                >
                  Contact us
                </button>{" "}
                to discuss availability. We're always looking to serve athletes
                throughout Northeast Ohio and neighboring communities.
              </p>
            </div>

            <button
              data-testid="mobile-book-btn"
              onClick={() => scrollTo("#assessment")}
              className="mt-8 w-full bg-white text-black uppercase tracking-[0.2em] font-bold text-sm py-5 hover:bg-white/85 transition-colors duration-300"
            >
              Book Free Evaluation
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
